import React, { useEffect, useState } from "react";
import { ToastContainer } from "react-toastify";
import {
  Box,
  Button,
  Typography,
  useTheme,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Container,
  Avatar
} from "@mui/material";
import {
  Search,
  LayoutGrid,
  List,
  Plus,
  Edit2,
  Trash2,
  HelpCircle,
  Info
} from 'lucide-react';
import { tokens } from "../../theme";
import Modal from "../../utils/components/Modal";
import Cancellation from "../../utils/components/Cancellation";
import FAQForm from "./FAQForm";
import { getAllFAQs, deleteFAQ, deleteFAQs, updateFAQ } from "./FAQService";
import { useLoadingSpinner } from "../../utils/components/LoadingSpinnerProvider";
import CustomDataTable from "../../utils/components/CustomDataTable";

const FAQs = () => {
  const [faqsData, setFAQsData] = useState({ content: [], totalElements: 0 });
  const [ids, setIds] = useState([]);
  const [FAQ, setFAQ] = useState(null);
  const [state, setState] = useState(false);
  const [params, setParams] = useState({ pageSize: 12, page: 0 });
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeletionModalOpen, setIsDeletionModalOpen] = useState(false);
  const setIsLoading = useLoadingSpinner();

  // High-Volume Optimization States
  const [viewMode, setViewMode] = useState('card');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('ALL');

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const lunchEditionModal = (faq) => {
    setFAQ(faq);
    setIsEditModalOpen(true);
  };

  const lunchDeletionModal = (selectedId) => {
    setIds([selectedId]);
    setIsDeletionModalOpen(true);
  };

  const deleteFAQHandler = async () => {
    setIsLoading(true);
    if (ids.length === 1) {
      await deleteFAQ(ids[0], setState, setIsLoading);
    } else if (ids.length > 1) {
      await deleteFAQs(ids, setState, setIsLoading);
    }
    setIsDeletionModalOpen(false);
  };

  useEffect(() => {
    setIsLoading(true);
    getAllFAQs(setFAQsData, setIsLoading, params);
  }, [state, params, setIsLoading]);

  const categories = ['ALL', ...new Set(faqsData.content.map(f => f.category))].filter(Boolean);

  const filteredFAQs = faqsData.content.filter(f => {
    const matchesSearch = f.question?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      f.answer?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'ALL' || f.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const columns = [
    {
      field: "question",
      headerName: "Question",
      width: 400,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap="12px">
          <Avatar sx={{ bgcolor: `${colors.blueAccent[500]}1A`, color: colors.blueAccent[500], width: 32, height: 32 }}>
            <HelpCircle size={18} />
          </Avatar>
          <Typography fontWeight="600" color={colors.grey[100]} noWrap>
            {params.value}
          </Typography>
        </Box>
      )
    },
    {
      field: "category",
      headerName: "Category",
      width: 150,
      renderCell: (params) => (
        <Chip
          label={params.value || "General"}
          size="small"
          sx={{ bgcolor: colors.primary[500], color: colors.grey[300], fontWeight: '600' }}
        />
      )
    },
    {
      field: "answer", headerName: "Answer Preview", width: 350, renderCell: (params) => (
        <Typography variant="body2" color={colors.grey[300]} noWrap sx={{ maxWidth: '100%' }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      renderCell: (params) => (
        <Box display="flex" gap="4px">
          <IconButton size="small" onClick={() => lunchEditionModal(params.row)} sx={{ color: colors.grey[100] }}>
            <Edit2 size={18} />
          </IconButton>
          <IconButton size="small" onClick={() => lunchDeletionModal(params.row.id)} sx={{ color: colors.redAccent[500] }}>
            <Trash2 size={18} />
          </IconButton>
        </Box>
      )
    }
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: "40px", pb: "80px" }}>
      {/* Enhanced Header Section */}
      <Box
        mb="40px"
        sx={{
          background: `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`,
          padding: "32px",
          borderRadius: "24px",
          border: `1px solid ${theme.palette.mode === "dark" ? colors.primary[400] : colors.grey[200]}`,
          boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb="24px">
          <Box>
            <Typography variant="h2" fontWeight="bold" sx={{ color: colors.grey[100], mb: "4px", fontFamily: "Josefin Sans" }}>
              Frequently Asked Questions
            </Typography>
            <Typography variant="h5" color={theme.palette.mode === 'dark' ? colors.greenAccent[500] : colors.greenAccent[300]} fontWeight="500">
              {faqsData.content.length} expertise entries compiled
            </Typography>
          </Box>

          <Box display="flex" gap="12px" alignItems="center">
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, next) => next && setViewMode(next)}
              size="small"
              sx={{
                bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
                borderRadius: '12px',
                p: '4px',
                '& .MuiToggleButton-root': {
                  border: 'none',
                  borderRadius: '8px',
                  color: colors.grey[300],
                  '&.Mui-selected': {
                    bgcolor: colors.greenAccent[500],
                    color: theme.palette.mode === "dark" ? colors.primary[500] : colors.primary[100],
                    '&:hover': { bgcolor: colors.greenAccent[400] }
                  }
                }
              }}
            >
              <ToggleButton value="card"><LayoutGrid size={18} /></ToggleButton>
              <ToggleButton value="list"><List size={18} /></ToggleButton>
            </ToggleButtonGroup>

            <Button
              variant="contained"
              startIcon={<Plus size={20} />}
              onClick={() => setIsCreateModalOpen(true)}
              sx={{
                backgroundColor: colors.greenAccent[500],
                color: theme.palette.mode === "dark" ? colors.primary[500] : colors.primary[100],
                fontWeight: "bold",
                padding: "10px 24px",
                borderRadius: "12px",
                textTransform: "none",
                boxShadow: `0 8px 20px ${colors.greenAccent[500]}33`,
                "&:hover": {
                  backgroundColor: colors.greenAccent[400],
                  transform: "translateY(-2px)"
                },
                transition: "all 0.2s"
              }}
            >
              Add FAQ
            </Button>
          </Box>
        </Box>

        <Box display="flex" gap="16px" alignItems="center">
          <TextField
            placeholder="Search questions or answers..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: '16px',
                bgcolor: theme.palette.mode === "dark" ? "rgba(0,0,0,0.2)" : "#FFFFFF",
                '& fieldset': { borderColor: theme.palette.mode === "dark" ? colors.primary[400] : colors.grey[200] }
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} color={colors.grey[300]} />
                </InputAdornment>
              ),
            }}
          />
          <Box display="flex" gap="8px" sx={{ overflowX: 'auto', pb: '4px' }}>
            {categories.map((cat) => (
              <Chip
                key={cat}
                label={cat}
                onClick={() => setCategoryFilter(cat)}
                sx={{
                  borderRadius: '10px',
                  fontWeight: '600',
                  bgcolor: categoryFilter === cat ? colors.blueAccent[500] : 'transparent',
                  color: categoryFilter === cat ? '#FFFFFF' : colors.grey[300],
                  border: `1px solid ${categoryFilter === cat ? colors.blueAccent[500] : colors.grey[400]}`,
                  '&:hover': { bgcolor: categoryFilter === cat ? colors.blueAccent[400] : 'rgba(255,255,255,0.05)' }
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>

      {/* Grid or List View */}
      {viewMode === 'card' ? (
        <Grid container spacing={3}>
          {filteredFAQs.map((item) => (
            <Grid item xs={12} lg={6} key={item.id}>
              <Card sx={{
                height: "100%",
                borderRadius: "24px",
                border: `1px solid ${theme.palette.mode === "dark" ? colors.primary[400] : colors.grey[200]}`,
                backgroundColor: theme.palette.mode === "dark" ? colors.primary[400] : "#FFFFFF",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                display: "flex",
                flexDirection: "column",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 12px 30px rgba(0,0,0,0.1)",
                  borderColor: colors.greenAccent[500]
                }
              }}>
                <CardContent sx={{ p: "24px", flexGrow: 1 }}>
                  <Box display="flex" gap="16px" mb="20px" alignItems="flex-start">
                    <Avatar sx={{ bgcolor: `${colors.blueAccent[500]}1A`, color: colors.blueAccent[500], borderRadius: "12px", width: 44, height: 44 }}>
                      <HelpCircle size={24} />
                    </Avatar>
                    <Box flex={1}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb="8px">
                        <Typography variant="h5" fontWeight="bold" color={colors.grey[100]}>
                          {item.question}
                        </Typography>
                        <Chip label={item.category || "General"} size="small" sx={{ bgcolor: colors.primary[500], fontWeight: 'bold' }} />
                      </Box>
                      <Typography variant="body2" color={colors.grey[300]} sx={{ lineHeight: 1.6 }}>
                        {item.answer}
                      </Typography>
                    </Box>
                  </Box>

                  {item.moreDetail && (
                    <Box sx={{ p: "16px", bgcolor: theme.palette.mode === "dark" ? colors.primary[500] : colors.grey[100], borderRadius: "12px", display: 'flex', gap: '12px', mb: '20px' }}>
                      <Info size={18} color={colors.greenAccent[500]} style={{ flexShrink: 0 }} />
                      <Typography variant="caption" color={colors.grey[400]} sx={{ fontStyle: 'italic' }}>
                        {item.moreDetail}
                      </Typography>
                    </Box>
                  )}

                  <Box mt="auto" pt="20px" borderTop={`1px solid ${theme.palette.mode === "dark" ? colors.primary[500] : colors.grey[100]}`} display="flex" justifyContent="space-between">
                    <Button
                      startIcon={<Edit2 size={16} />}
                      onClick={() => lunchEditionModal(item)}
                      sx={{
                        color: colors.grey[100],
                        textTransform: "none",
                        fontWeight: "600",
                        "&:hover": { color: colors.greenAccent[500] }
                      }}
                    >
                      Edit FAQ
                    </Button>
                    <IconButton
                      onClick={() => lunchDeletionModal(item.id)}
                      sx={{
                        color: colors.redAccent[500],
                        "&:hover": { bgcolor: `${colors.redAccent[500]}1A` }
                      }}
                    >
                      <Trash2 size={18} />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <CustomDataTable
          addRecordButtonTitle={"Add FAQ"}
          headerColumn={columns}
          lunchCreateModal={() => setIsCreateModalOpen(true)}
          paginationModel={params}
          rows={{ ...faqsData, content: filteredFAQs }}
          setPaginationModel={setParams}
          tableTitle={"FAQs List"}
        />
      )}

      {/* Modals */}
      <Modal isOpen={isEditModalOpen} onClose={setIsEditModalOpen}>
        <FAQForm
          faqToEdit={FAQ}
          isUpdating={true}
          setState={setState}
          toggleModal={setIsEditModalOpen}
          updateFAQ={updateFAQ}
        />
      </Modal>
      <Modal isOpen={isCreateModalOpen} onClose={setIsCreateModalOpen}>
        <FAQForm
          isUpdating={false}
          setState={setState}
          toggleModal={setIsCreateModalOpen}
        />
      </Modal>
      <Modal isOpen={isDeletionModalOpen} onClose={setIsDeletionModalOpen}>
        <Cancellation
          actionHandler={deleteFAQHandler}
          actionText={"Delete"}
          cancelText={"Go Back"}
          setOpen={setIsDeletionModalOpen}
          title={ids.length > 1 ? `Delete FAQs` : `Delete FAQ`}
          warningMessage={`Are you sure you want to delete ${ids.length > 1 ? 'these FAQs' : 'this FAQ'}? This action cannot be undone.`}
        />
      </Modal>
      <ToastContainer />
    </Container>
  );
};

export default FAQs;
